import type { TestDebrief } from '@@/api/learnings/tests/test-debrief';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import { can } from 'definitions/capabilities';
import { apiErrorMessage } from '../api-error';
import { useWebsites } from '../useWebsites';
import { learningsPageKey } from './useLearningsPage';

type UseTestDebriefParams = { experimentId: number };

/**
 * One concluded test as the Learnings debrief shows it, and the two things the team can do there:
 * stand by the card (review), and include a test its debrief flagged once the problem is fixed.
 * Including changes the evidence, so it reloads the page too. Both are for the roles that can write.
 */
export const useTestDebrief = ({ experimentId }: UseTestDebriefParams) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { website } = useWebsites();
  const websiteId: number = website?.id ?? 0;
  const queryKey = ['LEARNINGS_TEST', websiteId, experimentId];

  const { data, isLoading, isError } = useQuery<TestDebrief>({
    queryKey,
    enabled: !!websiteId && experimentId > 0 && website?.learningsEnabled === true,
    queryFn: () => client.get<TestDebrief>(`/learnings/${websiteId}/tests/${experimentId}`),
  });

  const review = useMutation({
    mutationFn: (reviewed: boolean) =>
      client.post(`/learnings/${websiteId}/tests/${experimentId}/review`, { reviewed }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
    onError: (error) => toast.error(apiErrorMessage(error, 'Could not save the review.')),
  });

  const include = useMutation({
    mutationFn: (value: boolean) =>
      client.post(`/learnings/${websiteId}/tests/${experimentId}/include`, { include: value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: learningsPageKey(websiteId) });
    },
    onError: (error) => toast.error(apiErrorMessage(error, 'Could not include the test.')),
  });

  return {
    debrief: data ?? null,
    isLoading,
    isError,
    canWrite: can(user, 'experiments:create'),
    setReviewed: (value: boolean) => review.mutate(value),
    isReviewing: review.isPending,
    setIncluded: (value: boolean) => include.mutate(value),
    isIncluding: include.isPending,
  };
};

export type TestDebriefManager = ReturnType<typeof useTestDebrief>;
